// src/pages/Customers.js

import React, { useReducer, useEffect } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  Paper,
  Typography,
  CircularProgress,
  Toolbar as MuiToolbar,
} from '@mui/material';
import CustomerRow from './CustomerRow';
import Pagination from '../components/Pagination';
import CustomersTableHead from '../components/CustomersTableHead';
import { initialState, customersReducer } from '../reducers/customersReducer';
import { handleSortRequest } from "../utils/sortingUtils";
import { ACTIONS } from "../reducers/listActions";
import api from "../services/api";

// Define columns array
const columns = [
  { id: 'redirect', label: '', sortable: false },
  { id: 'firstName', label: 'First Name', sortable: true },
  { id: 'lastName', label: 'Last Name', sortable: true },
  { id: 'address', label: 'Address', sortable: false },
  { id: 'phone1', label: 'Phone 1', sortable: false },
  { id: 'phone2', label: 'Phone 2', sortable: false },
  { id: 'email', label: 'Email', sortable: false },
  { id: 'comments', label: '', sortable: false },
  { id: 'imageUrl', label: '', sortable: false },
];

function Customers({ data }) {
  const [state, dispatch] = useReducer(customersReducer, initialState);
  const {
    customers,
    loading,
    error,
    order,
    orderBy,
    page,
    rowsPerPage,
    totalCustomers,
  } = state;

  const isSearchMode = !!data;
  const displayedCustomers = isSearchMode ? data : customers;

  useEffect(() => {
    if (isSearchMode) return; // ✅ Skip fetching if in search mode

    const fetchCustomers = async () => {
      dispatch({ type: ACTIONS.FETCH_INIT });
      try {
        const token = localStorage.getItem('token'); // Retrieve the token
        if (!token) throw new Error('No token found');

        const response = await api.get('/customers', {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: page + 1, // Backend expects 1-based page index
            limit: rowsPerPage,
            sortBy: orderBy,
            order: order,
          },
        });

        dispatch({
          type: ACTIONS.FETCH_SUCCESS,
          payload: {
            customers: response.data.data, // Adjusted to extract 'data' from response
            total: response.data.total, // Extract 'total' from response
          },
        });
      } catch (err) {
        console.error('Error fetching customers:', err);
        dispatch({ type: ACTIONS.FETCH_FAILURE, payload: 'Failed to fetch customers.' });
      }
    };

    fetchCustomers();
  }, [page, rowsPerPage, order, orderBy, isSearchMode]);

  const handleSort = handleSortRequest(orderBy, order, dispatch);

  const handleChangePage = (event, newPage) => {
    dispatch({ type: ACTIONS.SET_PAGE, payload: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: ACTIONS.SET_ROWS_PER_PAGE,
      payload: parseInt(event.target.value, 10),
    });
  };

  return (
    <>
      <MuiToolbar />
      <Paper sx={{ padding: '1rem', width: '97%', margin: '1rem auto' }}>
        <Typography variant="h4" gutterBottom>
          Customers
        </Typography>
        {loading ? (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <CircularProgress />
          </div>
        ) : error ? (
          <Typography variant="h6" color="error" align="center">
            {error}
          </Typography>
        ) : displayedCustomers.length === 0 ? (
          <Typography>No customers found.</Typography>
        ) : (
          <>
            {/* Top Pagination */}
            {!isSearchMode && (
              <Pagination
                count={totalCustomers}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}

            <Table>
              <TableHead>
                <CustomersTableHead
                  columns={columns}
                  order={order}
                  orderBy={orderBy}
                  onSortRequest={handleSort} />
              </TableHead>
              <TableBody>
                {displayedCustomers.map((customer) => (
                  <CustomerRow key={customer.id} customer={customer} customers={displayedCustomers} />
                ))}
              </TableBody>
            </Table>

            {/* Bottom Pagination */}
            {!isSearchMode && (
              <Pagination
                count={totalCustomers}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </>
        )}
      </Paper>
    </>
  );
}

export default Customers;